/**
 * The types of messages available
 */
export enum MessageType {
	/**
	 * The `init` message should be listened by the _Interactive_
	 * to know how to render itself.
	 *
	 * Its message payload includes:
	 * - the last known state of the _Interactive_
	 */
	Init = 'init',

	/**
	 * The `navigate` event is emitted when the user wants to navigate
	 * to a different step.
	 *
	 * Its message payload includes:
	 * - the number of the target step to navigate to
	 */
	Navigate = 'navigate',

	/**
	 * `StepCompleted` event is triggered by the _Interactive_ once the
	 * __completion criteria__ of the currently active step has been fulfilled.
	 *
	 * Its message payload includes:
	 * - the updated state of the interactive
	 * - the number of the completed step
	 */
	StepCompleted = 'step_completed',

	/**
	 * `Error` event is triggered by the _Interactive_ when an error happens.
	 *
	 * Its message payload includes:
	 * - the error thrown
	 */
	Error = 'error',
}

/**
 * The payload map for each message type.
 *
 * Example:
 *
 * ```typescript
 * const p: MessagePayload[MessageType.Init] = { ... }
 * ```
 */
export interface MessagePayload {
	[MessageType.Init]: {
		/**
		 * JSON string containing the last
		 * known state of the interactive. This is
		 * set to `undefined` when no previous state is known.
		 */
		state?: string;
	};

	[MessageType.Navigate]: {
		/**
		 * The number of the target step.
		 */
		step: number;
	};

	[MessageType.StepCompleted]: {
		/**
		 * The updated internal state of the Interactive,
		 * passed as a JSON string. Set to `undefined` when
		 * the state is not stored for this interactive.
		 *
		 * This state should contain the state of the __Full Interactive__
		 * not only the state of the currently completed step. This is
		 * to avoid coupling between the _Interactive_ and _InteractiveFrame_.
		 *
		 * From the _InteractiveFrame_ we simply pass this state to the API for
		 * storage _as it is_.
		 */
		state?: string;

		/**
		 * The number of the step completed
		 */
		step: number;
	};

	[MessageType.Error]: {
		/**
		 * The error that has been thrown in the Interactive.
		 */
		error: Error;
	};
}
